/* eslint-disable max-len */
import React, { FC, useRef } from "react";
import {
  CircularProgress,
  IconButton,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from "@mui/material";
import { Control, Controller, useWatch } from "react-hook-form";
import { AddIconNew, CloseIcon, SendIcon } from "../../../../components/Icons";
import SupportInfoBanner from "../../../../components/SupportInfoBanner/SupportInfoBanner";
import SupportModalHeader from "../../../../components/SupportModalHeader/SupportModalHeader";
import {
  ALLOWED_FILE_EXTENSIONS,
  COPY,
  MAX_DESCRIPTION_LENGTH,
} from "../constants";
import { SupportTicketMappingOption } from "../../../../services/customer-support/customer-support.interface";
import { CustomerSupportFormValues } from "../../../../hooks/useCustomerSupportForm";
import { defaultTheme } from "../../../../styles/muiTheme";
import SupportActionButton from "../../../../components/SupportActionButton/SupportActionButton";

interface TicketFormStepProps {
  control: Control<CustomerSupportFormValues>;
  attachment: File | null;
  fileError: string | null;
  loading: boolean;
  isFormValid: boolean;
  subjectOptions: SupportTicketMappingOption[];
  onFileSelect: (file: File | null) => void;
  onRemoveFile: () => void;
  onSubmit: () => void;
}

const inputSx = {
  "backgroundColor": "white.main",
  "borderRadius": "8px",
  "fontSize": "14px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "taupe.dark",
    borderWidth: "0.524px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "taupe.light",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "taupe.light",
    borderWidth: "1px",
  },
};

const TicketFormStep: FC<TicketFormStepProps> = ({
  control,
  attachment,
  fileError,
  loading,
  isFormValid,
  subjectOptions,
  onFileSelect,
  onRemoveFile,
  onSubmit,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const descriptionValue = useWatch({ control, name: "description" }) || "";

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    onFileSelect(file);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="flex flex-col items-center w-full flex-1 min-h-0">
      <SupportModalHeader
        heading={COPY.STEPPER.HEADING}
        description={COPY.STEPPER.DESCRIPTION}
        filledSegments={2}
      />

      <div className="flex-1 overflow-y-auto scrollbar-thin w-full mt-4">
        <div className="flex flex-col gap-6">
          {/* Subject */}
          <div className="border border-taupe-50 rounded-xl p-4 flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <Typography
                className="grenette-pro"
                sx={{ fontSize: "18px", lineHeight: "24px", fontWeight: 600 }}
              >
                {COPY.FORM.SUBJECT_TITLE}
                <span className="text-red-2">*</span>
              </Typography>
              <Typography sx={{ fontSize: "14px", lineHeight: "20px", color: "gray.dark" }}>
                {COPY.FORM.SUBJECT_SUBTITLE}
              </Typography>
            </div>

            <Controller
              name="subject"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  displayEmpty
                  input={<OutlinedInput />}
                  renderValue={(selected) => {
                    if (!selected) {
                      return <span style={{ color: defaultTheme.palette.gray.dark }}>{COPY.FORM.SUBJECT_PLACEHOLDER}</span>;
                    }
                    const match = subjectOptions.find((opt) => opt.value === selected);
                    return <span>{match?.name ?? String(selected)}</span>;
                  }}
                  sx={{
                    ...inputSx,
                    height: "46px",
                  }}
                >
                  {subjectOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </div>

          {/* Description */}
          <div className="border border-taupe-50 rounded-xl p-4 flex flex-col gap-4">
            <Typography
              className="grenette-pro"
              sx={{ fontSize: "18px", lineHeight: "24px", fontWeight: 600 }}
            >
              {COPY.FORM.DESCRIPTION_TITLE}
              <span className="text-red-2">*</span>
            </Typography>

            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <div className="relative">
                  <OutlinedInput
                    {...field}
                    multiline
                    minRows={8}
                    placeholder={COPY.FORM.DESCRIPTION_PLACEHOLDER}
                    inputProps={{ maxLength: MAX_DESCRIPTION_LENGTH }}
                    fullWidth
                    sx={{
                      ...inputSx,
                      alignItems: "flex-start",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "gray.dark",
                      textAlign: "right",
                      mt: 0.5,
                    }}
                  >
                    {descriptionValue.length}/{MAX_DESCRIPTION_LENGTH}
                  </Typography>
                </div>
              )}
            />
          </div>

          {/* File Upload */}
          <div className="border border-taupe-50 rounded-xl p-4 flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <Typography
                className="grenette-pro"
                sx={{ fontSize: "18px", lineHeight: "24px", fontWeight: 600 }}
              >
                {COPY.FORM.UPLOAD_TITLE}
              </Typography>
              <Typography sx={{ fontSize: "14px", lineHeight: "20px", color: "gray.dark" }}>
                {COPY.FORM.UPLOAD_SUBTITLE}
              </Typography>
            </div>

            <div className="flex flex-col gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept={ALLOWED_FILE_EXTENSIONS}
                onChange={handleFileChange}
                className="hidden"
              />

              {attachment ? (
                <div className="flex items-center justify-between bg-white border border-taupe-50 rounded-lg px-4 py-3">
                  <Typography sx={{ fontSize: "14px", color: "black.main" }} noWrap className="flex-1 mr-2">
                    {attachment.name}
                  </Typography>
                  <IconButton size="small" onClick={onRemoveFile}>
                    <CloseIcon />
                  </IconButton>
                </div>
              ) : (
                <OutlinedInput
                  readOnly
                  placeholder={COPY.FORM.UPLOAD_PLACEHOLDER}
                  onClick={handleFileClick}
                  endAdornment={<AddIconNew />}
                  sx={{
                    ...inputSx,
                    "height": "46px",
                    "cursor": "pointer",
                    "& input": { cursor: "pointer" },
                  }}
                />
              )}

              <Typography sx={{ fontSize: "14px", lineHeight: "20px" }}>
                <span className="text-red-2">*</span>
                {COPY.FORM.UPLOAD_SIZE_HINT}
              </Typography>

              {fileError && (
                <Typography sx={{ fontSize: "13px", color: "red.dark" }}>
                  {fileError}
                </Typography>
              )}
            </div>
          </div>

          <SupportInfoBanner />
        </div>
      </div>

      <div className="w-full pt-6 shrink-0">
        <SupportActionButton
          variant="contained"
          color="primary"
          fullWidth
          disabled={!isFormValid || loading}
          startIcon={loading ?
            <CircularProgress size={20} color="inherit" /> :
            <SendIcon width={24} height={24} color="currentColor" />
          }
          onClick={onSubmit}
        >
          {COPY.FORM.SUBMIT_BUTTON}
        </SupportActionButton>
      </div>
    </div>
  );
};

export default TicketFormStep;
